import React, { useContext, useState } from 'react';
import { IStackTokens, Icon, Stack, TooltipHost } from '@fluentui/react';
import { DefaultHubButton } from '../../../HubLayout/components/Utilities/DefaultHubButton';
import { ThemeContext } from '../../../HubLayout/models/ThemeContext';
import { getThemeFromString } from '../../../Common/util/localStorageUtils';

interface ICopyReleaseDataButtonProps {
    /**
     * The raw release JSON captured on initial page load. When undefined the
     * button is disabled (data not yet available).
     */
    releaseJson?: string;
}

/**
 * Admin-only button that copies the raw SRHS (AGSS) pipeline release JSON
 * (captured on initial page load) to the clipboard. Issues no request.
 */
export const CopyReleaseDataButton: React.FC<ICopyReleaseDataButtonProps> = (
    props: ICopyReleaseDataButtonProps
) => {
    const themeContext = useContext(ThemeContext);
    const theme = getThemeFromString(themeContext.themeName);

    const [copySucceeded, setCopySucceeded] = useState<boolean | undefined>(
        undefined
    );
    const stackTokens: IStackTokens = { childrenGap: 10 };

    const handleCopyClick = async () => {
        if (props.releaseJson === undefined) {
            return;
        }

        try {
            await navigator.clipboard.writeText(props.releaseJson);
            setCopySucceeded(true);
        } catch {
            setCopySucceeded(false);
        }
    };

    return (
        <div className="CopyReleaseDataButton-root">
            <Stack
                horizontal
                tokens={stackTokens}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                <TooltipHost content="Copy the raw SRHS release data JSON to the clipboard">
                    <DefaultHubButton
                        buttonText="Copy Release JSON"
                        icon="Copy"
                        onClick={handleCopyClick}
                        disabled={props.releaseJson === undefined}
                    />
                </TooltipHost>

                {copySucceeded !== undefined && (
                    <Icon
                        iconName={
                            copySucceeded
                                ? 'SkypeCircleCheck'
                                : 'StatusErrorFull'
                        }
                        style={{
                            color: copySucceeded
                                ? theme.semanticColors.successIcon
                                : theme.semanticColors.errorIcon,
                        }}
                    />
                )}
            </Stack>
        </div>
    );
};
